import { createSlice } from '../../core';
import type { CameraSlice } from '../../core/camera';
import type { Point2D } from '../../core/utils';
import type { PanSlice } from '../pan';

const EDGE_PAN_THRESHOLD = 50;
const MAX_PAN_SPEED = 15;

const calculateEdgePanVelocity = (
  viewportPoint: Point2D,
  viewportRect: { width: number; height: number }
): { x: number; y: number } => {
  let vx = 0;
  let vy = 0;

  if (viewportPoint.x < EDGE_PAN_THRESHOLD) {
    vx =
      -((EDGE_PAN_THRESHOLD - viewportPoint.x) / EDGE_PAN_THRESHOLD) *
      MAX_PAN_SPEED;
  } else if (viewportPoint.x > viewportRect.width - EDGE_PAN_THRESHOLD) {
    vx =
      ((viewportPoint.x - (viewportRect.width - EDGE_PAN_THRESHOLD)) /
        EDGE_PAN_THRESHOLD) *
      MAX_PAN_SPEED;
  }

  if (viewportPoint.y < EDGE_PAN_THRESHOLD) {
    vy =
      -((EDGE_PAN_THRESHOLD - viewportPoint.y) / EDGE_PAN_THRESHOLD) *
      MAX_PAN_SPEED;
  } else if (viewportPoint.y > viewportRect.height - EDGE_PAN_THRESHOLD) {
    vy =
      ((viewportPoint.y - (viewportRect.height - EDGE_PAN_THRESHOLD)) /
        EDGE_PAN_THRESHOLD) *
      MAX_PAN_SPEED;
  }

  return { x: vx, y: vy };
};

type AutoPanDeps = CameraSlice & PanSlice;

type AutoPanState = {
  isActive: boolean;
  mouseViewportPos: Point2D | null;
  animationFrameId: number | null;
};

const autoPanDefaultState: AutoPanState = {
  isActive: false,
  mouseViewportPos: null,
  animationFrameId: null,
};

export type AutoPanSlice = {
  autoPan: {
    state: AutoPanState;
    actions: {
      start: (mouseViewportPos: Point2D) => void;
      updatePosition: (mouseViewportPos: Point2D) => void;
      stop: () => void;
    };
  };
};

export const createAutoPanSlice = createSlice<AutoPanDeps>()<AutoPanSlice>((
  set,
  get
) => {
  const setState = (
    next: AutoPanState | ((prev: AutoPanState) => AutoPanState)
  ) => {
    set(root => {
      const prev = root.autoPan.state;
      const state = typeof next === 'function' ? next(prev) : next;
      return {
        ...root,
        autoPan: {
          ...root.autoPan,
          state,
        },
      };
    });
  };

  return {
    autoPan: {
      state: autoPanDefaultState,
      actions: {
        start: mouseViewportPos => {
          const root = get();
          const { autoPan, canvas } = root;
          const autoPanState = autoPan.state;

          // Already running with same position
          if (
            autoPanState.isActive &&
            autoPanState.mouseViewportPos === mouseViewportPos
          ) {
            return;
          }

          // Stop existing animation if any
          if (autoPanState.animationFrameId !== null) {
            cancelAnimationFrame(autoPanState.animationFrameId);
          }

          const { viewportRect } = canvas;
          const velocity = calculateEdgePanVelocity(
            mouseViewportPos,
            viewportRect
          );

          // Only start if there's actual panning needed
          if (velocity.x === 0 && velocity.y === 0) {
            setState({
              isActive: false,
              mouseViewportPos: null,
              animationFrameId: null,
            });
            return;
          }

          const pan = () => {
            const current = get();
            const currentAutoPan = current.autoPan.state;

            if (!currentAutoPan.isActive || !currentAutoPan.mouseViewportPos) {
              return;
            }

            const currentViewportRect = current.canvas.viewportRect;
            const currentVelocity = calculateEdgePanVelocity(
              currentAutoPan.mouseViewportPos,
              currentViewportRect
            );

            if (currentVelocity.x !== 0 || currentVelocity.y !== 0) {
              // Delegate to Pan feature
              // This ensures we respect Pan bounds and configuration
              current.pan.actions.panBy({
                x: currentVelocity.x,
                y: currentVelocity.y,
              });

              const frameId = requestAnimationFrame(pan);
              setState(prev => ({
                ...prev,
                animationFrameId: frameId,
              }));
            } else {
              setState({
                isActive: false,
                mouseViewportPos: null,
                animationFrameId: null,
              });
            }
          };

          const frameId = requestAnimationFrame(pan);
          setState({
            isActive: true,
            mouseViewportPos,
            animationFrameId: frameId,
          });
        },
        updatePosition: mouseViewportPos => {
          setState(prev => {
            if (!prev.isActive) return prev;
            return {
              ...prev,
              mouseViewportPos,
            };
          });
        },
        stop: () => {
          const { autoPan } = get();
          if (autoPan.state.animationFrameId !== null) {
            cancelAnimationFrame(autoPan.state.animationFrameId);
          }
          setState({
            isActive: false,
            mouseViewportPos: null,
            animationFrameId: null,
          });
        },
      },
    },
  };
});
